// Sitemap generator for APN website
import { SEO_CONFIG } from '../services/seoService';

// Static pages configuration
const STATIC_PAGES = [
  {
    url: '/',
    changefreq: 'daily',
    priority: '1.0',
    lastmod: new Date().toISOString()
  },
  {
    url: '/about',
    changefreq: 'monthly',
    priority: '0.8',
    lastmod: new Date().toISOString()
  },
  {
    url: '/about/membership',
    changefreq: 'weekly',
    priority: '0.9',
    lastmod: new Date().toISOString()
  },
  {
    url: '/about/sponsorship',
    changefreq: 'monthly',
    priority: '0.7',
    lastmod: new Date().toISOString()
  },
  {
    url: '/about/contact',
    changefreq: 'monthly',
    priority: '0.6',
    lastmod: new Date().toISOString()
  },
  {
    url: '/events',
    changefreq: 'daily',
    priority: '0.9',
    lastmod: new Date().toISOString()
  },
  {
    url: '/jobs',
    changefreq: 'daily',
    priority: '0.9',
    lastmod: new Date().toISOString()
  },
  {
    url: '/projects',
    changefreq: 'daily',
    priority: '0.8',
    lastmod: new Date().toISOString()
  },
  {
    url: '/directory',
    changefreq: 'weekly',
    priority: '0.8',
    lastmod: new Date().toISOString()
  },
  {
    url: '/groups',
    changefreq: 'weekly',
    priority: '0.8',
    lastmod: new Date().toISOString()
  },
  {
    url: '/news',
    changefreq: 'daily',
    priority: '0.7',
    lastmod: new Date().toISOString()
  },
  {
    url: '/login',
    changefreq: 'monthly',
    priority: '0.5',
    lastmod: new Date().toISOString()
  },
  {
    url: '/signup',
    changefreq: 'monthly',
    priority: '0.6',
    lastmod: new Date().toISOString()
  }
];

// Generate sitemap XML
export const generateSitemap = async (dynamicData = {}) => {
  const { events = [], jobs = [], projects = [], articles = [], users = [] } = dynamicData;
  
  let sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1"
        xmlns:news="http://www.google.com/schemas/sitemap-news/0.9">`;

  // Add static pages
  STATIC_PAGES.forEach(page => {
    sitemap += `
  <url>
    <loc>${SEO_CONFIG.siteUrl}${page.url}</loc>
    <lastmod>${page.lastmod}</lastmod>
    <changefreq>${page.changefreq}</changefreq>
    <priority>${page.priority}</priority>
  </url>`;
  });

  // Add dynamic event pages
  events.forEach(event => {
    sitemap += `
  <url>
    <loc>${SEO_CONFIG.siteUrl}/events/${event.id}</loc>
    <lastmod>${event.updatedAt || event.createdAt}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>`;
    
    if (event.image) {
      sitemap += `
    <image:image>
      <image:loc>${SEO_CONFIG.siteUrl}${event.image}</image:loc>
      <image:title>${event.title}</image:title>
      <image:caption>${event.description?.substring(0, 100)}...</image:caption>
    </image:image>`;
    }
    
    sitemap += `
  </url>`;
  });

  // Add dynamic job pages
  jobs.forEach(job => {
    sitemap += `
  <url>
    <loc>${SEO_CONFIG.siteUrl}/jobs/${job.id}</loc>
    <lastmod>${job.updatedAt || job.postedDate}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`;
  });

  // Add dynamic project pages
  projects.forEach(project => {
    sitemap += `
  <url>
    <loc>${SEO_CONFIG.siteUrl}/projects/${project.id}</loc>
    <lastmod>${project.updatedAt || project.postedDate}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.7</priority>
  </url>`;
  });

  // Add news/article pages
  articles.forEach(article => {
    sitemap += `
  <url>
    <loc>${SEO_CONFIG.siteUrl}/news/${article.slug || article.id}</loc>
    <lastmod>${article.updatedAt || article.publishedAt}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>`;
    
    if (article.image) {
      sitemap += `
    <image:image>
      <image:loc>${SEO_CONFIG.siteUrl}${article.image}</image:loc>
      <image:title>${article.title}</image:title>
      <image:caption>${article.excerpt?.substring(0, 100)}...</image:caption>
    </image:image>`;
    }
    
    // Add news-specific markup for recent articles
    const publishDate = new Date(article.publishedAt);
    const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
    
    if (publishDate > threeDaysAgo) {
      sitemap += `
    <news:news>
      <news:publication>
        <news:name>${SEO_CONFIG.siteName}</news:name>
        <news:language>en</news:language>
      </news:publication>
      <news:publication_date>${article.publishedAt}</news:publication_date>
      <news:title>${article.title}</news:title>
      <news:keywords>${article.category || 'finance,professional,networking'}</news:keywords>
    </news:news>`;
    }
    
    sitemap += `
  </url>`;
  });

  // Add public user profile pages (only approved members)
  users.filter(user => user.status === 'approved' && user.isPublic !== false).forEach(user => {
    sitemap += `
  <url>
    <loc>${SEO_CONFIG.siteUrl}/directory/${user.id}</loc>
    <lastmod>${user.updatedAt || user.createdAt}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.5</priority>
  </url>`;
  });

  sitemap += `
</urlset>`;

  return sitemap;
};

// Generate robots.txt content
export const generateRobotsTxt = () => {
  return `# https://www.robotstxt.org/robotstxt.html
User-agent: *
Allow: /

# Disallow admin and private areas
Disallow: /admin/
Disallow: /dashboard/
Disallow: /profile/edit
Disallow: /api/
Disallow: /test/

# Allow important pages
Allow: /events/
Allow: /jobs/
Allow: /projects/
Allow: /news/
Allow: /directory/
Allow: /groups/
Allow: /about/

# Sitemap location
Sitemap: ${SEO_CONFIG.siteUrl}/sitemap.xml

# Crawl delay (be respectful)
Crawl-delay: 1`;
};

// Save sitemap to public folder (for build process)
export const saveSitemap = async (dynamicData) => {
  const sitemapContent = await generateSitemap(dynamicData);
  const robotsContent = generateRobotsTxt();
  
  // In a real implementation, you would save these files to the public folder
  // For now, we'll return the content for manual saving or build process integration
  return {
    sitemap: sitemapContent,
    robots: robotsContent
  };
};

// Generate sitemap index for large sites
export const generateSitemapIndex = (sitemaps = []) => {
  let sitemapIndex = `<?xml version="1.0" encoding="UTF-8"?>
<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`;

  sitemaps.forEach(sitemap => {
    sitemapIndex += `
  <sitemap>
    <loc>${SEO_CONFIG.siteUrl}/${sitemap.filename}</loc>
    <lastmod>${sitemap.lastmod || new Date().toISOString()}</lastmod>
  </sitemap>`;
  });

  sitemapIndex += `
</sitemapindex>`;

  return sitemapIndex;
};

export default {
  generateSitemap,
  generateRobotsTxt,
  saveSitemap,
  generateSitemapIndex
};
